/**
 * authGuard — platform-mode route protection (employee-app pattern).
 *
 * Demo mode (PLATFORM_MODE=false, the default everywhere today): immediate
 * `true` — no auth, no redirects, demo behaviour untouched.
 *
 * Platform mode: bootstrap tokens from the login-web hash handoff
 * (#rt=…&t=…), accept an existing access token, otherwise try one refresh;
 * failing that, hard-redirect to login-web with the return path. A stale
 * access token is fine here — the gql client's 401-refresh-retry catches it
 * on first use. Once authed, kick off the current-user, current-customer and
 * operator's-division fetches so the shell (top-bar avatar/identity + the
 * division's or customer's logo/name) fills in
 * — fire-and-forget, they must not delay navigation, and the signals update the
 * top bar reactively when they land.
 */

import { inject } from '@angular/core';
import type { CanActivateChildFn, CanActivateFn } from '@angular/router';

import { VisitorsAuthService } from './auth.service';
import { CompanyService } from './company.service';
import { DivisionService } from './division.service';
import { RDX_VISITORS_ENV } from './env';
import { ViewerService } from './viewer.service';

export const authGuard: CanActivateFn & CanActivateChildFn = async (_route, routerState) => {
  if (!RDX_VISITORS_ENV.PLATFORM_MODE) return true;

  const auth = inject(VisitorsAuthService);
  const viewer = inject(ViewerService);
  const company = inject(CompanyService);
  const division = inject(DivisionService);

  auth.bootstrapFromHash();

  let authed = !!auth.accessToken();
  if (!authed) authed = await auth.refresh();
  if (!authed) {
    auth.loginRedirect(routerState.url);
    return false;
  }

  // Load the live identity + customer once (single-flight + cached).
  // Fire-and-forget: nothing gates on them, so don't block first paint — the
  // avatar/name and customer logo/name fill in reactively when they resolve.
  void viewer.ensureLoaded();
  void company.ensureLoaded();
  void division.ensureLoaded();

  return true;
};
